import React, { useState, useEffect } from 'react';
import { Plus, Settings, CheckCircle, Send, DollarSign, TrendingUp, AlertCircle, RefreshCw, Wallet, Percent, Building2, Hash } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { useWallet } from '@solana/wallet-adapter-react';
import { ApiService, Company, Transaction, CompanyWallet } from '../../services/api';
import { SignedTransactionService } from '../../services/signedTransactionService';
import { usePagination } from '../../hooks/usePagination';
import toast from 'react-hot-toast';

// Import new components
import CreateCompanyModal from '../../components/modals/admin/CreateCompanyModal';
import AddFundsModal from '../../components/modals/admin/AddFundsModal';
import UpdateCompanyLimitsModal from '../../components/modals/admin/UpdateCompanyLimitsModal';
import InitializePlatformModal from '../../components/modals/admin/InitializePlatformModal';
import SettingsModal from '../../components/modals/admin/SettingsModal';
import TransactionDetailsModal from '../../components/modals/TransactionDetailsModal';
import AllowedCompaniesModal from '../../components/modals/admin/AllowedCompaniesModal';
import ApproveCompanyWalletModal from '../../components/modals/admin/ApproveCompanyWalletModal';
import RejectWalletRequestModal from '../../components/modals/admin/RejectWalletRequestModal';
import CompaniesTable from '../../components/tables/admin/CompaniesTable';
import AdminTransactionsTable from '../../components/tables/admin/AdminTransactionsTable';
import CompanyWalletsTable from '../../components/tables/admin/CompanyWalletsTable';
import CompanyWalletRequestsTable from '../../components/tables/admin/CompanyWalletRequestsTable';

interface PlatformInfo {
  id: string;
  adminWallet: string;
  isInitialized: boolean;
  totalValue: number;
  totalCompanies: number;
  totalTransactions: number;
  protocolFeeRate: number;
  createdAt: string;
  updatedAt: string;
  solanaSignature?: string;
}

interface CompanyWalletRequest {
  id: string;
  walletAddress: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  company: {
    id: string;
    companyName: string;
    companyEmail: string;
  };
}


const AdminSettlements: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showInitializeModal, setShowInitializeModal] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [selectedCompanyForFunds, setSelectedCompanyForFunds] = useState<Company | null>(null);
  const [showCompanyLimitsModal, setShowCompanyLimitsModal] = useState(false);
  const [selectedCompanyForLimits, setSelectedCompanyForLimits] = useState<Company | null>(null);
  const [showAllowedCompaniesModal, setShowAllowedCompaniesModal] = useState(false);
  const [selectedCompanyForAllowed, setSelectedCompanyForAllowed] = useState<Company | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingWalletId, setApprovingWalletId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companyWallets, setCompanyWallets] = useState<CompanyWallet[]>([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [companyWalletRequests, setCompanyWalletRequests] = useState<CompanyWalletRequest[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingWalletId, setRejectingWalletId] = useState<string | null>(null);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [isUpdatingLimits, setIsUpdatingLimits] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Date filtering state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Pagination hooks
  const companiesPagination = usePagination(companies, { itemsPerPage: 10 });
  const transactionsPagination = usePagination(transactions, { itemsPerPage: 10 });
  
  const { } = useAuth();
  const { connected, publicKey, signTransaction } = useWallet();

  // Sort handler
  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
  };

  // Date filter handlers
  const handleStartDateChange = (date: string | undefined) => {
    setStartDate(date || '');
  };

  const handleEndDateChange = (date: string | undefined) => {
    setEndDate(date || '');
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [sortBy, sortOrder, startDate, endDate]);

  const fetchData = async () => {
    if(platformInfo === null) {
      setLoading(true);
    }
    
    try {
      const [companiesData, transactionsData, platformData, walletRequestsData] = await Promise.allSettled([
        ApiService.getCompanies(),
        ApiService.getTransactions(undefined, undefined, sortBy || undefined, sortOrder, startDate && startDate.trim() !== '' ? startDate : undefined, endDate && endDate.trim() !== '' ? endDate : undefined),
        ApiService.getPlatformState(),
        ApiService.getCompanyWalletRequests()
      ]);

      if (companiesData.status === 'fulfilled') {
        setCompanies(companiesData.value);
      }
      if (transactionsData.status === 'fulfilled') {
        setTransactions(transactionsData.value);
      }
      if (platformData.status === 'fulfilled') {
        setPlatformInfo(platformData.value);
      }
      if (walletRequestsData.status === 'fulfilled') {
        setCompanyWalletRequests(walletRequestsData.value);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    
    try {
      // Call the backend to refresh/sync platform state with on-chain data
      await ApiService.refreshPlatformState();
      toast.success('Platform state refreshed successfully!');
      
      // Fetch updated data
      await fetchData();
    } catch (error: any) {
      console.error('Failed to refresh platform state:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh platform state');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCreateCompany = async (data: {
    companyWallet: string;
    companyName: string;
    companyEmail: string;
    companyAddress: string;
    companyPhone: string;
    initialAmount: string;
  }) => {
    setIsCreatingCompany(true);
    try {
      await ApiService.createCompany({
        ...data,
        initialAmount: Math.round(parseFloat(data.initialAmount) * 100),
        minTransactionAmount: 10000, // $100
        maxTransactionAmount: 100000000, // $1,000,000
      });

      toast.success('Company created successfully!');
      setShowCreateCompany(false);
      await fetchData();
    } catch (error: any) {
      console.error('Error creating company:', error);
      toast.error(error.message || 'Failed to create company');
    } finally {
      setIsCreatingCompany(false);
    }
  };

  const handleUpdateSettings = async (data: { protocolFeeRate: string }) => {
    setIsUpdatingSettings(true);
    
    // Check if wallet is connected
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet to update settings');
      setIsUpdatingSettings(false);
      return;
    }

    try {
      // Create wallet object for signing
      const wallet = {
        publicKey,
        signTransaction,
      };

      await ApiService.updateFeeRates({
        protocolFeeRate: parseFloat(data.protocolFeeRate),
      }, wallet);

      toast.success('Settings updated successfully');
      setShowSettings(false);
      await fetchData();
    } catch (error: any) {
      console.error('Error updating settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleInitializePlatform = async (data: { protocolFeeRate: string }) => {
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    const wallet = {
      publicKey,
      signTransaction,
    };

    setInitializing(true);
    try {
      await ApiService.initializePlatform(
        parseInt(data.protocolFeeRate),
        wallet
      );

      toast.success('Platform initialized successfully!');
      setShowInitializeModal(false);
      await fetchData();
    } catch (error: any) {
      console.error('Error initializing platform:', error);
      toast.error(error.message || 'Failed to initialize platform');
    } finally {
      setInitializing(false);
    }
  };

  const handleAddFunds = async (data: { amount: string }) => {
    if (!selectedCompanyForFunds) return;

    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    const wallet = {
      publicKey,
      signTransaction,
    };

    setIsAddingFunds(true);
    try {
      await ApiService.addFundsToCompany(
        selectedCompanyForFunds.id, 
        Math.round(parseFloat(data.amount) * 100),
        wallet
      );

      toast.success('Funds added successfully!');
      setShowAddFundsModal(false);
      setSelectedCompanyForFunds(null);
      await fetchData();
    } catch (error: any) {
      console.error('Error adding funds:', error);
      toast.error(error.message || 'Failed to add funds');
    } finally {
      setIsAddingFunds(false);
    }
  };

  const handleUpdateCompanyLimits = async (data: {
    minTransactionAmount: string;
    maxTransactionAmount: string;
    dailyLimit: string;
  }) => {
    if (!selectedCompanyForLimits) return;

    setIsUpdatingLimits(true);
    
    // Check if wallet is connected
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet to update company limits');
      setIsUpdatingLimits(false);
      return;
    }

    const wallet = {
      publicKey,
      signTransaction,
    };

    try {
      // Build the transaction
      const transactionData = await ApiService.buildUpdateCompanyLimitsTransaction(
        selectedCompanyForLimits.id,
        parseFloat(data.minTransactionAmount) * 100, // Convert to cents
        parseFloat(data.maxTransactionAmount) * 100, // Convert to cents
        parseFloat(data.dailyLimit) * 100, // Convert to cents
        publicKey.toString()
      );

      // Sign the transaction
      const signedTransaction = await SignedTransactionService.signAndSendTransaction(
        transactionData,
        wallet
      );

      if (signedTransaction.success && signedTransaction.signature) {
        // Process the result
        await ApiService.processUpdateCompanyLimitsResult(
          selectedCompanyForLimits.id,
          parseFloat(data.minTransactionAmount) * 100,
          parseFloat(data.maxTransactionAmount) * 100,
          parseFloat(data.dailyLimit) * 100,
          signedTransaction.signature,
          true
        );

        toast.success('Company limits updated successfully');
        setShowCompanyLimitsModal(false);
        setSelectedCompanyForLimits(null);
        await fetchData();
      } else {
        toast.error('Failed to sign transaction');
      }
    } catch (error: any) {
      console.error('Error updating company limits:', error);
      toast.error(error.response?.data?.message || 'Failed to update company limits');
    } finally {
      setIsUpdatingLimits(false);
    }
  };

  const handleViewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleViewOnSolscan = (transaction: Transaction) => {
    if (transaction.solanaSignature) {
      window.open(`https://solscan.io/tx/${transaction.solanaSignature}?cluster=devnet`, '_blank');
    }
  };


  const handleRefreshCompanyData = async (companyId: string) => {
    try {
      await ApiService.syncCompanyDataForAdmin(companyId);
      toast.success('Company data refreshed successfully!');
      await fetchData();
    } catch (error: any) {
      console.error('Error refreshing company data:', error);
      toast.error(error.message || 'Failed to refresh company data');
    }
  };

  const handleApproveCompany = async (companyId: string) => {
    try {
      await ApiService.approveCompany(companyId, true);
      toast.success('Company approved successfully!');
      await fetchData();
    } catch (error: any) {
      console.error('Error approving company:', error);
      toast.error(error.message || 'Failed to approve company');
    }
  };

  const handleSuspendCompany = async (companyId: string) => {
    try {
      await ApiService.approveCompany(companyId, false);
      toast.success('Company suspended successfully');
      await fetchData();
    } catch (error: any) {
      console.error('Error suspending company:', error);
      toast.error(error.response?.data?.message || 'Failed to suspend company');
    }
  };

  const handleManageAllowedCompanies = async (company: Company) => {
    setSelectedCompanyForAllowed(company);
    setShowAllowedCompaniesModal(true);
  };


  const handleConfirmApprove = async (initialAmount: number) => {
    if (!approvingWalletId) return;

    try {
      // Create wallet object for signing
      const wallet = {
        publicKey,
        signTransaction
      };

      await ApiService.approveCompanyWallet(approvingWalletId, initialAmount * 100, wallet);
      toast.success(`Company wallet request approved and PDA created on-chain with ${initialAmount} initial amount!`);
      
      // Close modal and refresh data
      setShowApproveModal(false);
      setApprovingWalletId(null);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to approve company wallet:', error);
      toast.error(error.response?.data?.message || 'Failed to approve company wallet');
    }
  };

  const handleCompanySelect = async (companyId: string) => {
    setSelectedCompanyId(companyId);
    if (companyId) {
      setLoadingWallets(true);
      try {
        const wallets = await ApiService.getCompanyWalletsForAdmin(companyId);
        setCompanyWallets(wallets);
      } catch (error: any) {
        console.error('Failed to fetch company wallets:', error);
        toast.error('Failed to fetch company wallets');
        setCompanyWallets([]);
      } finally {
        setLoadingWallets(false);
      }
    } else {
      setCompanyWallets([]);
    }
  };

  const handleToggleWalletActive = async (walletId: string) => {
    if (!selectedCompanyId) return;
    
    try {
      await ApiService.toggleWalletActiveStatus(selectedCompanyId, walletId);
      toast.success('Wallet active status updated successfully!');
      
      // Refresh the wallets list
      const wallets = await ApiService.getCompanyWalletsForAdmin(selectedCompanyId);
      setCompanyWallets(wallets);
    } catch (error: any) {
      console.error('Failed to toggle wallet active status:', error);
      toast.error(error.response?.data?.message || 'Failed to update wallet status');
    }
  };

  const handleApproveWalletRequest = (walletId: string) => {
    // Check if wallet is connected
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet to approve company wallet requests');
      return;
    }

    // Set the wallet ID and show the modal
    setApprovingWalletId(walletId);
    setShowApproveModal(true);
  };

  const handleRejectWalletRequest = (walletId: string) => {
    setRejectingWalletId(walletId);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async (rejectionReason: string) => {
    if (!rejectingWalletId) return;

    try {
      await ApiService.rejectCompanyWallet(rejectingWalletId, rejectionReason);
      toast.success('Company wallet request rejected successfully!');
      
      // Close modal and refresh data
      setShowRejectModal(false);
      setRejectingWalletId(null);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to reject company wallet request:', error);
      toast.error(error.response?.data?.message || 'Failed to reject company wallet request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E131A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2777EF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E131A]">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Settlements</h1>
              <p className="text-sm sm:text-base text-gray-400 mt-1 sm:mt-2">Manage platform companies and transactions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 sm:space-y-0 w-full sm:w-auto">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-full sm:w-auto px-4 py-2 bg-[#182233] hover:bg-[#567095]/20 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowCreateCompany(true)}
                className="w-full sm:w-auto px-4 py-2 bg-[#2777EF] hover:bg-[#0053E2] text-white font-medium rounded-lg transition-colors flex items-center justify-center sm:justify-start"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Company
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="w-full sm:w-auto px-4 py-2 bg-[#182233] hover:bg-[#567095]/20 text-white font-medium rounded-lg transition-colors flex items-center justify-center sm:justify-start"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </button>
            </div>
          </div>
        </div>

        {/* Platform Status */}
        <div className="bg-[#182233] shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              {platformInfo?.isInitialized ? (
                <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
              ) : (
                <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
              )}
              <h3 className="text-lg leading-6 font-medium text-white">
                Loop-Settle Platform Status
              </h3>
            </div>
            
            <div className="mt-4">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                platformInfo?.isInitialized 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {platformInfo?.isInitialized ? 'Initialized' : 'Not Initialized'}
              </span>
            </div>

            {!platformInfo?.isInitialized && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Platform Not Initialized</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      The loop-settle program needs to be initialized before you can manage settlements. 
                      Click "Initialize Platform" to set up the platform with your admin wallet.
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setShowInitializeModal(true)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Initialize Platform
                  </button>
                </div>
              </div>
            )}

            {platformInfo?.isInitialized && (
              <div className="mt-4 bg-[#182233] shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-600">
                  <p className="text-sm text-gray-400 mt-1">
                    The loop-settle program is initialized and ready for operations.
                  </p>
                </div>
                
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Wallet className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">Admin Wallet</p>
                        <p className="text-sm text-gray-400 break-all">
                          {platformInfo.adminWallet || 'Not available'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Percent className="h-5 w-5 text-green-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">Protocol Fee Rate</p>
                        <p className="text-sm text-gray-400">
                          {platformInfo.protocolFeeRate ? `${(platformInfo.protocolFeeRate / 100).toFixed(2)}%` : 'Not set'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <DollarSign className="h-5 w-5 text-yellow-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">Total Value</p>
                        <p className="text-sm text-gray-400">
                          {formatCurrency(platformInfo.totalValue)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Building2 className="h-5 w-5 text-indigo-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">Total Companies</p>
                        <p className="text-sm text-gray-400">
                          {platformInfo.totalCompanies.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <Hash className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white">Total Transactions</p>
                        <p className="text-sm text-gray-400">
                          {platformInfo.totalTransactions.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Total Companies</dt>
                    <dd className="text-lg font-medium text-white">{companies.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Active Companies</dt>
                    <dd className="text-lg font-medium text-white">
                      {companies.filter(c => c.status === 'ACTIVE').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#182233] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Send className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">Pending Transactions</dt>
                    <dd className="text-lg font-medium text-white">
                      {transactions.filter(t => t.status === 'PENDING').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Companies Section */}
        <div className="bg-[#182233] shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-white mb-4">Company Management</h3>
            <CompaniesTable
              companies={companiesPagination.paginatedData}
              onRefreshCompanyData={handleRefreshCompanyData}
              onAddFunds={(company) => {
                setSelectedCompanyForFunds(company);
                setShowAddFundsModal(true);
              }}
              onUpdateCompanyLimits={(company) => {
                setSelectedCompanyForLimits(company);
                setShowCompanyLimitsModal(true);
              }}
              onManageAllowedCompanies={(company) => handleManageAllowedCompanies(company)}
              onApproveCompany={handleApproveCompany}
              onSuspendCompany={handleSuspendCompany}
              onSelectCompany={handleCompanySelect}
              selectedCompanyId={selectedCompanyId}
              currentPage={companiesPagination.currentPage}
              totalPages={companiesPagination.totalPages}
              totalItems={companiesPagination.totalItems}
              itemsPerPage={companiesPagination.itemsPerPage}
              onPageChange={companiesPagination.setCurrentPage}
              onItemsPerPageChange={companiesPagination.setItemsPerPage}
              showPagination={true}
            />
          </div>
        </div>

        {/* Company Wallet Requests Section */}
        {companyWalletRequests.length > 0 && (
          <div className="bg-[#182233] shadow rounded-lg mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-white mb-4">Pending Company Wallet Requests</h3>
              <CompanyWalletRequestsTable
                requests={companyWalletRequests}
                onApprove={handleApproveWalletRequest}
                onReject={handleRejectWalletRequest}
              />
            </div>
          </div>
        )}

        {/* Company Wallets Section */}
        {selectedCompanyId && (
          <div className="bg-[#182233] shadow rounded-lg mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-white mb-4">Company Wallets</h3>
              <CompanyWalletsTable
                wallets={companyWallets}
                loading={loadingWallets}
                onToggleActive={handleToggleWalletActive}
              />
            </div>
          </div>
        )}

        {/* Transactions Section */}
        <div className="bg-[#182233] shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-white mb-4">Transaction Management</h3>
            <AdminTransactionsTable
              transactions={transactionsPagination.paginatedData}
              onViewDetails={handleViewTransactionDetails}
              onViewOnSolscan={handleViewOnSolscan}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onClearDateFilter={handleClearDateFilter}
              showDateFilter={true}
              currentPage={transactionsPagination.currentPage}
              totalPages={transactionsPagination.totalPages}
              totalItems={transactionsPagination.totalItems}
              itemsPerPage={transactionsPagination.itemsPerPage}
              onPageChange={transactionsPagination.setCurrentPage}
              onItemsPerPageChange={transactionsPagination.setItemsPerPage}
              showPagination={true}
            />
          </div>
        </div>

        {/* Modals */}
        <CreateCompanyModal
          isOpen={showCreateCompany}
          onClose={() => setShowCreateCompany(false)}
          onSubmit={handleCreateCompany}
          isLoading={isCreatingCompany}
        />

        <AddFundsModal
          isOpen={showAddFundsModal}
          onClose={() => {
            setShowAddFundsModal(false);
            setSelectedCompanyForFunds(null);
          }}
          onSubmit={handleAddFunds}
          company={selectedCompanyForFunds}
          isLoading={isAddingFunds}
        />

        <UpdateCompanyLimitsModal
          isOpen={showCompanyLimitsModal}
          onClose={() => {
            setShowCompanyLimitsModal(false);
            setSelectedCompanyForLimits(null);
          }}
          onSubmit={handleUpdateCompanyLimits}
          company={selectedCompanyForLimits}
          isLoading={isUpdatingLimits}
        />

        <InitializePlatformModal
          isOpen={showInitializeModal}
          onClose={() => setShowInitializeModal(false)}
          onSubmit={handleInitializePlatform}
          isLoading={initializing}
        />

        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          onSubmit={handleUpdateSettings}
          isLoading={isUpdatingSettings}
        />

        <TransactionDetailsModal
          isOpen={showTransactionDetails}
          onClose={() => setShowTransactionDetails(false)}
          transaction={selectedTransaction}
          onViewOnSolscan={handleViewOnSolscan}
        />

        <AllowedCompaniesModal
          isOpen={showAllowedCompaniesModal}
          onClose={() => {
            setShowAllowedCompaniesModal(false);
            setSelectedCompanyForAllowed(null);
          }}
          selectedCompany={selectedCompanyForAllowed}
          companies={companies}
          onRefresh={fetchData}
        />

        <ApproveCompanyWalletModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setApprovingWalletId(null);
          }}
          onConfirm={handleConfirmApprove}
        />

        <RejectWalletRequestModal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRejectingWalletId(null);
          }}
          onConfirm={handleConfirmReject}
        />
      </div>
    </div>
  );
};

export default AdminSettlements;
